import { postalCodeRegionConfig } from '@cash-design-system/react'

import { regex } from './regex'
import { validTLDs } from './tlds'

export const isValidEmail = (email: string): boolean => {
  return (
    /^[A-Z0-9._%'=+][A-Z0-9._%'=+-]*@([A-Z0-9]([A-Z0-9-]*[A-Z0-9])?\.)+[A-Z]{2,20}$/i.test(email) &&
    validTLDs.includes((email.split('.').pop() || '').toUpperCase())
  ) // TLD check
}

export const isAmex = (cardNumber: string): boolean => {
  return regex.cardAmex.test(cardNumber)
}

export const isFirstDigitsAmex = (cardNumber: string): boolean => {
  return cardNumber ? cardNumber.slice(0, 2) === '34' || cardNumber.slice(0, 2) === '37' : false
}

export const isValidPostalCode = (value: string, region?: keyof typeof postalCodeRegionConfig) => {
  if (region) {
    return postalCodeRegionConfig[region].regex.test(value)
  }
  return Object.values(postalCodeRegionConfig).some(
    (config: (typeof postalCodeRegionConfig)[keyof typeof postalCodeRegionConfig]) => config.regex.test(value)
  )
}

export const isValidCardNumber = (cardNumber: string): boolean => {
  // TODO add luhn algo
  const number = cardNumber.replace(/\s/g, '')
  const cvvLength = isAmex(cardNumber) ? 15 : 16
  const validationRegEx = new RegExp(`^\\d{${cvvLength}}$`)
  return validationRegEx.test(number)
}

const parseExpirationDate = (date: string) => {
  const matches = date.replace(/\//g, '').match(regex.expirationDate)
  if (!matches) {
    return null
  }

  const year = parseInt(matches[2], 10)
  const month = parseInt(matches[1], 10)

  return { month, year }
}

const isDatePast = ({ month, year }: { month: number; year: number }) => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear() % 100
  const currentMonth = currentDate.getMonth() + 1

  return year < currentYear || (year === currentYear && month < currentMonth)
}

export type ExpirationDateValidationError = 'invalidDate' | 'pastDate'
export const isValidExpirationDate = (date: string): { isValid: boolean; reason?: ExpirationDateValidationError } => {
  const expirationMonthYear = parseExpirationDate(date)
  if (!expirationMonthYear) {
    return { isValid: false, reason: 'invalidDate' }
  }

  const isPast = isDatePast(expirationMonthYear)
  return { isValid: !isPast, reason: isPast ? 'pastDate' : undefined }
}

export const isValidCvv = (cvv: string, cardNumber: string): boolean => {
  const cvvLength = isAmex(cardNumber) ? 4 : 3
  const validationRegEx = new RegExp(`^\\d{${cvvLength}}$`)
  return validationRegEx.test(cvv)
}

// Expects `birthdate` to be in format `mm/dd/yyyy`
export const isValidBirthdate = (birthdate: string): boolean => {
  return isValidFormattedDate(birthdate) && isDateBeforeToday(birthdate)
}

// `formattedDate` expects to be in the format `mm/dd/yyyy`
export const isValidFormattedDate = (formattedDate: string): boolean => {
  const [month, day, year] = formattedDate.split('/')
  if (!year || !month || !day) {
    return false
  }

  // Values must be padded
  if (year.length !== 4 || month.length !== 2 || day.length !== 2) {
    return false
  }

  const lastDayOfMonth = new Date(parseInt(year), parseInt(month), 0)
  const daysInTheMonth = lastDayOfMonth.getDate()
  if (daysInTheMonth < parseInt(day, 10)) {
    return false
  }

  const date = new Date(formattedDate)
  if (!(date instanceof Date) || isNaN(date.getDate())) {
    return false
  }

  return true
}

// `formattedDate` expects to be in the format `mm/dd/yyyy`
export const isDateBeforeToday = (formattedDate: string): boolean => {
  const [month, day, year] = formattedDate.split('/')
  if (!year || !month || !day) {
    return false
  }

  const date = new Date(formattedDate)
  if (!(date instanceof Date) || isNaN(date.getDate())) {
    return false
  }

  const now = new Date()

  const birthdateDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0)
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
  if (birthdateDate >= todayDate) {
    return false
  }

  return true
}

// bank routing validation
export const isValidRoutingNumberUsa = (routingNumber: string) => {
  return /^\d{9}$/.test(routingNumber)
}
export const isValidRoutingNumberGb = (routingNumber: string) => {
  return /^\d{6}$/.test(routingNumber)
}
export const isValidBsbAu = (bsbNumber: string) => {
  return /^\d{6}$/.test(bsbNumber)
}
export const isValidInstitutiontNumberCa = (institutionNumber: string) => {
  return /^\d{3}$/.test(institutionNumber)
}
export const isValidTransitNumberCa = (transitNumber: string) => {
  return /^\d{5}$/.test(transitNumber)
}

// bank account validation
export const isValidBankAccountNumberUsa = (number: string) => {
  return /^\d{4,17}$/.test(number)
}

export const isValidBankAccountNumberCad = (number: string) => {
  return /^\d{4,12}$/.test(number)
}

export const isValidBankAccountNumberAus = (number: string) => {
  return /^\d{6,10}$/.test(number)
}

export const isValidBankAccountNumberGb = (number: string) => {
  return /^\d{7,9}$/.test(number)
}

//phone number validation
export const isValidPhoneNumber = (number: string, regionCode?: 'US' | 'GB' | 'CA'): boolean => {
  if (!number) {
    return false
  }
  number = number.replace(/\(|\)|-|\s/g, '')

  const usCaRegexTest = /^\+?1\d{10}$|^[2-9]\d{9}$/.test(number)
  const ukRegexTest = /^0\d{10}$|^\+?44[1-9]\d{9}$|^\+?440\d{10}$/.test(number)
  const intlRegexTest = /^\+\d{11,13}$/.test(number)

  if (regionCode === 'US' || regionCode === 'CA') {
    return usCaRegexTest
  }
  if (regionCode === 'GB') {
    return ukRegexTest
  }

  return usCaRegexTest || ukRegexTest || intlRegexTest
}

const currencySymbolsPrefix = new RegExp('^[$£]')
export const isCashtag = (text: string): boolean => {
  return !!text && currencySymbolsPrefix.test(text)
}

export const isNonEmptyString = (value: string) => {
  return value && /\S/.test(value)
}

export const isNumericAndLengthOf = (length: number, value: string) => {
  const re = new RegExp(`^\\d{${length}}$`)
  return !!value && re.test(value.replace(/-/g, ''))
}

export const isValidFullSsnOrEin = (tin: string) => {
  return tin && isNumericAndLengthOf(9, tin)
}

// Character validation
export const isAlphanumeric = (value: string): boolean => {
  return regex.alphanumeric.test(value)
}

export const containsAnAlphaCharacter = (value: string): boolean => {
  return regex.containsAnAlphaCharacter.test(value)
}
